/**
 * This module contains the various links used by our Apollo client.
 */
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { ApolloLink, HttpOptions, createHttpLink, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { getToken } from '~/util/authentication';
import { environment } from '../util/environment';
import { debugLink } from '~/hooks/useQueryWithDebug';

/** Link: adds the auth token to request headers. */
const authLink = setContext( ( _, { headers } ) => {
  const token = getToken();

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
} );

/**
 * Support batching of queries if the component asks for it via the context parameter of use*, e.g.
 * `useQuery( fooQuery, { batch: true } )`
 *
 * Docs:
 * - https://www.apollographql.com/docs/react/api/link/apollo-link-batch-http/
 * - https://www.apollographql.com/docs/react/api/link/introduction/#directional-composition
 */
function enableBatching( link: ApolloLink, options: BatchHttpLink.Options & HttpOptions ) {
  return link.split(
    ( operation ) => operation.getContext().batch === true,
    new BatchHttpLink( options ),
    createHttpLink( options ),
  );
}

/** Returns all links we want for our Apollo client. */
export function getLinkChain( withDebug: boolean ) {
  const uri = environment.graphqlUrl;

  return enableBatching( withDebug ? from( [debugLink, authLink] ) : from( [authLink] ), { uri } );
}
